import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { X, ArrowRight, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

export default function TopUp() {
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [minsLeft, setMinsLeft] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payments/topups");
        setPacks(data.packs || []);
        const popular = (data.packs || []).find((p) => p.badge) || (data.packs || [])[0];
        setSelected(popular?.key || null);
      } catch { toast.error("Couldn't load top-up packs."); }
      try {
        const { data } = await api.get("/usage");
        setMinsLeft(Math.floor((data.talk_remaining_seconds || 0) / 60));
      } catch {}
      setLoading(false);
    })();
  }, []);

  const checkout = async () => {
    if (!selected || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/payments/topup/checkout", { key: selected, origin_url: window.location.origin });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      throw new Error("no url");
    } catch { toast.error("Couldn't start checkout. Please try again."); setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden" data-testid="topup-page">
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[560px] h-[560px] rounded-full opacity-40"
        style={{ background: "radial-gradient(circle, hsl(28 90% 62% / 0.35) 0%, transparent 62%)", filter: "blur(80px)" }} />

      <div className="relative max-w-md mx-auto px-6 pt-8 pb-40 flex flex-col min-h-screen">
        <button onClick={() => navigate(-1)} data-testid="topup-close"
          className="w-11 h-11 rounded-full bg-white/8 backdrop-blur flex items-center justify-center text-foreground/80 hover:bg-white/15 transition-colors">
          <X className="w-5 h-5" />
        </button>

        <p className="mt-6 text-xs uppercase tracking-[0.3em] text-primary">Top up</p>
        <h1 className="font-serif text-5xl leading-tight mt-3">Add more time.</h1>
        <p className="text-muted-foreground mt-4 text-base leading-relaxed">
          Pick a pack and keep going. <span className="text-foreground font-medium">Minutes never expire.</span>
        </p>
        {minsLeft != null && (
          <p className="text-sm text-muted-foreground mt-2" data-testid="topup-balance">You have <span className="text-foreground font-medium">{minsLeft} min</span> left right now.</p>
        )}

        <div className="mt-auto space-y-4 pt-10">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : packs.map((p, i) => {
            const active = selected === p.key;
            return (
              <motion.button key={p.key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                onClick={() => setSelected(p.key)} data-testid={`topup-pack-${p.key}`}
                className={`relative w-full text-left rounded-2xl px-5 py-5 border transition-all ${active ? "border-primary bg-primary/10 shadow-[0_0_0_1px_hsl(var(--primary))]" : "border-border bg-card/40 hover:bg-card/70"}`}>
                {p.badge && (
                  <span className="absolute -top-3 right-4 text-[11px] font-medium px-3 py-1 rounded-full bg-primary text-primary-foreground">{p.badge}</span>
                )}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xl font-semibold">{p.minutes} Minutes</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{p.per_min}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-semibold">{p.price}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">never expires</p>
                  </div>
                </div>
                {active && <Check className="absolute bottom-4 right-4 w-4 h-4 text-primary" />}
              </motion.button>
            );
          })}
        </div>
      </div>

      <div className="fixed bottom-0 inset-x-0 px-6 pb-8 pt-4 bg-gradient-to-t from-background via-background to-transparent">
        <div className="max-w-md mx-auto">
          <Button onClick={checkout} disabled={!selected || busy} data-testid="topup-continue"
            className="w-full rounded-full h-14 text-base bg-gradient-to-r from-primary to-amber-400 text-primary-foreground hover:opacity-90">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Continue <ArrowRight className="w-5 h-5 ml-2" /></>}
          </Button>
          <div className="flex justify-center gap-8 mt-4 text-xs text-muted-foreground">
            <button onClick={() => navigate("/legal")}>Terms</button>
            <button onClick={() => navigate("/legal")}>Privacy</button>
          </div>
        </div>
      </div>
    </div>
  );
}
